// ═══════════════════════════════════════════════════════════════
// Forms — Published Forms
// Quick-access list of all published forms with copy-link buttons
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput,
  Table, ActionIcon, Tooltip, CopyButton,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconLink, IconCheck, IconEye, IconChartBar,
  IconWorld, IconLock,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useFormsStore } from '../../store/formsStore';
import type { FormSchema } from '../../types/forms';

function getFieldCount(schema: string): number {
  try {
    const s: FormSchema = JSON.parse(schema);
    return s.pages.reduce((sum, p) => sum + p.fields.filter(f => f.type !== 'section' && f.type !== 'instructions' && f.type !== 'page_break').length, 0);
  } catch { return 0; }
}

export default function FormPublished() {
  const navigate = useNavigate();
  const { templates, responses, loadTemplates, loadResponses } = useFormsStore();
  const [search, setSearch] = useState('');

  useEffect(() => { loadTemplates(); loadResponses(); }, []);

  const published = useMemo(() => {
    return templates
      .filter(t => t.status === 'published')
      .filter(t => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (t.templateName || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));
  }, [templates, search]);

  const getResponseCount = (templateId: number) =>
    responses.filter(r => String(r.templateId) === String(templateId)).length;

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Published Forms</Title>
          <Text size="xs" c="dimmed">{published.length} form{published.length !== 1 ? 's' : ''} live</Text>
        </div>
        <TextInput
          placeholder="Search forms..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          w={260}
          size="xs"
        />
      </Group>

      {published.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder ta="center">
          <IconWorld size={40} color="var(--mantine-color-dimmed)" style={{ opacity: 0.3, marginBottom: 8 }} />
          <Text fw={500} c="dimmed">No published forms yet</Text>
          <Text size="xs" c="dimmed">Publish a form from the Builder to see it here.</Text>
        </Paper>
      ) : (
        <Paper withBorder radius="sm" style={{ overflow: 'hidden' }}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Form Name</Table.Th>
                <Table.Th>Fields</Table.Th>
                <Table.Th>Responses</Table.Th>
                <Table.Th>Visibility</Table.Th>
                <Table.Th>Public Link</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {published.map(t => {
                const fields = getFieldCount(t.schema);
                const respCount = getResponseCount(t.id);
                const isPublic = t.isPublic === 'true';
                const publicUrl = isPublic && t.publicSlug
                  ? `${window.location.origin}/forms/public/${t.publicSlug}`
                  : null;

                return (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{t.templateName}</Text>
                      {t.description && <Text size="xs" c="dimmed" lineClamp={1}>{t.description}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light">{fields}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>{respCount}</Text>
                    </Table.Td>
                    <Table.Td>
                      {isPublic ? (
                        <Badge size="xs" color="cyan" leftSection={<IconWorld size={10} />}>Public</Badge>
                      ) : (
                        <Badge size="xs" color="gray" leftSection={<IconLock size={10} />}>Internal</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {publicUrl ? (
                        <CopyButton value={publicUrl}>
                          {({ copied, copy }) => (
                            <Button
                              size="compact-xs"
                              variant={copied ? 'filled' : 'light'}
                              color={copied ? 'green' : 'violet'}
                              leftSection={copied ? <IconCheck size={12} /> : <IconLink size={12} />}
                              onClick={copy}
                            >
                              {copied ? 'Copied!' : 'Copy Link'}
                            </Button>
                          )}
                        </CopyButton>
                      ) : (
                        <Group gap={4}>
                          <Button variant="light" size="compact-xs" onClick={() => navigate(`/forms/preview/${t.id}`)}>
                            Fill Out
                          </Button>
                          <CopyButton value={`${window.location.origin}/forms/preview/${t.id}`}>
                            {({ copy, copied }) => (
                              <Button variant="subtle" size="compact-xs" color={copied ? 'green' : 'gray'} onClick={copy}>
                                {copied ? 'Copied!' : 'Copy Link'}
                              </Button>
                            )}
                          </CopyButton>
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="Preview">
                          <ActionIcon variant="subtle" size="sm"
                            onClick={() => navigate(`/forms/preview/${t.id}`)}>
                            <IconEye size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="View Responses">
                          <ActionIcon variant="subtle" size="sm"
                            onClick={() => navigate('/forms/responses')}>
                            <IconChartBar size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </div>
  );
}
